/* ============================================================
   CARE — Product hero / buy box behaviour
   Custom elements: <care-gallery> and <care-buy-box>
   ============================================================ */
(function () {
  'use strict';

  /* ---------- Money formatting (Shopify-compatible) ---------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    format = format || '${{amount}}';

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents = parts[1] ? decimal + parts[1] : '';
      return dollars + cents;
    }

    switch ((format.match(placeholderRegex) || [])[1]) {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ''); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      default: value = withDelimiters(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  }

  /* ---------- Gallery ---------- */
  class CareGallery extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.stage = this.querySelector('.care-gallery__stage');
      this.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-media-target]');
        if (!thumb) return;
        this.show(thumb.getAttribute('data-media-target'), thumb);
      });
    }
    show(id, thumb) {
      this.querySelectorAll('.care-gallery__slide').forEach((s) => {
        s.classList.toggle('is-active', s.getAttribute('data-media-id') === id);
      });
      this.querySelectorAll('.care-gallery__thumb').forEach((t) => t.classList.remove('is-active'));
      if (thumb) thumb.classList.add('is-active');
    }
  }
  if (!customElements.get('care-gallery')) customElements.define('care-gallery', CareGallery);

  /* ---------- Buy box ---------- */
  class CareBuyBox extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      const json = this.querySelector('[data-care-product-json]');
      try { this.product = JSON.parse(json.textContent); } catch (e) { this.product = { variants: [] }; }

      this.form = this.querySelector('form');
      this.variantInput = this.querySelector('[data-care-variant-id]');
      this.planInput = this.querySelector('[data-care-selling-plan]');
      this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
      this.days = parseInt(this.getAttribute('data-per-day-days'), 10) || 30;
      this.perDaySuffix = this.getAttribute('data-per-day-suffix') || '/jour';

      this.currentVariant = this.product.variants.find((v) => String(v.id) === String(this.variantInput.value))
        || this.product.variants[0];

      this.bindOptions();
      this.bindOffers();
      this.bindQuantity();
      this.update();
    }

    bindOptions() {
      this.optionInputs = Array.prototype.slice.call(this.querySelectorAll('.care-variant__input'));
      this.optionInputs.forEach((input) => {
        input.addEventListener('change', () => this.onVariantChange());
      });
    }

    onVariantChange() {
      const selected = [];
      this.querySelectorAll('.care-variant').forEach((group, i) => {
        const checked = group.querySelector('.care-variant__input:checked');
        if (checked) selected[i] = checked.value;
      });
      const match = this.product.variants.find((v) => {
        return selected.every((val, i) => v.options[i] === val);
      });
      if (match) {
        this.currentVariant = match;
        this.variantInput.value = match.id;
      }
      this.update();
    }

    bindOffers() {
      this.offerInputs = Array.prototype.slice.call(this.querySelectorAll('.care-offer__input'));
      this.offerInputs.forEach((input) => {
        input.addEventListener('change', () => this.onOfferChange());
      });
      this.selectedOffer = this.querySelector('.care-offer__input:checked') || this.offerInputs[0];
    }

    onOfferChange() {
      this.selectedOffer = this.querySelector('.care-offer__input:checked');
      const planId = this.selectedOffer ? this.selectedOffer.getAttribute('data-plan-id') : '';
      if (planId) {
        this.planInput.value = planId;
        this.planInput.disabled = false;
      } else {
        this.planInput.value = '';
        this.planInput.disabled = true;
      }
      this.update();
    }

    bindQuantity() {
      const input = this.querySelector('[data-care-qty]');
      if (!input) return;
      const minus = this.querySelector('[data-qty-minus]');
      const plus = this.querySelector('[data-qty-plus]');
      if (minus) minus.addEventListener('click', () => { input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1); });
      if (plus) plus.addEventListener('click', () => { input.value = (parseInt(input.value, 10) || 1) + 1; });
    }

    /* Current unit price in cents given selected offer (plan allocation aware) */
    currentPriceCents() {
      const v = this.currentVariant;
      if (!v) return 0;
      const planId = this.selectedOffer && this.selectedOffer.getAttribute('data-plan-id');
      if (planId && v.selling_plan_allocations) {
        const alloc = v.selling_plan_allocations.find((a) => String(a.selling_plan_id) === String(planId));
        if (alloc) return alloc.price;
      }
      return v.price;
    }

    update() {
      const v = this.currentVariant;
      const submitText = this.querySelector('[data-care-submit-text]');
      const submitBtn = this.querySelector('.care-buybox__submit');
      const perDayEl = this.querySelector('[data-care-perday]');
      const stockEl = this.querySelector('[data-care-stock]');
      const dot = this.querySelector('.care-buybox__stock-dot');

      const available = v && v.available;
      if (submitBtn) submitBtn.disabled = !available;

      /* Per-day text */
      if (perDayEl) {
        const staticPerDay = this.selectedOffer && this.selectedOffer.getAttribute('data-perday');
        if (staticPerDay) {
          perDayEl.textContent = staticPerDay;
        } else {
          const cents = this.currentPriceCents();
          const perDay = Math.round(cents / this.days);
          perDayEl.textContent = formatMoney(perDay, this.moneyFormat) + ' ' + this.perDaySuffix;
        }
      }

      /* Offer price for plan/one-time rows (recompute on variant change) */
      this.querySelectorAll('.care-offer').forEach((offer) => {
        const input = offer.querySelector('.care-offer__input');
        const priceEl = offer.querySelector('[data-offer-price]');
        if (!priceEl || !input) return;
        const planId = input.getAttribute('data-plan-id');
        if (planId && v && v.selling_plan_allocations) {
          const alloc = v.selling_plan_allocations.find((a) => String(a.selling_plan_id) === String(planId));
          priceEl.textContent = formatMoney(alloc ? alloc.price : v.price, this.moneyFormat);
        } else if (input.hasAttribute('data-perday-from-price') && v) {
          priceEl.textContent = formatMoney(v.price, this.moneyFormat);
        }
      });

      /* Stock */
      if (dot) dot.classList.toggle('is-out', !available);
    }
  }
  if (!customElements.get('care-buy-box')) customElements.define('care-buy-box', CareBuyBox);
})();
